import { PreferenceGroupSubTypeEnum } from 'api-hooks/preference-tag';
import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import preferenceColumn from 'components/elements/table/editable-columns/preference-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetAirlineColumn from 'hooks/use-get-airline-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewAirline } from '../../hooks/use-get-default-values';
import {
  TicketOrderFormType,
  TicketOrderFlightPurchaseAirlineFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetScheduleAirlineTableList(): IColumn<TicketOrderFlightPurchaseAirlineFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const airlineColumn = useGetAirlineColumn();
  return [
    {
      header: t('common:airline'),
      accessorKey: 'airlineId',
      minSize: 150,
      dataType: ColumnDataType.select,
      selectProps: airlineColumn,
      required: true,
    },
    {
      header: t('common:date'),
      accessorKey: 'date',
      minSize: 100,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:detail'),
      accessorKey: 'detail',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:class'),
      accessorKey: 'preferenceId',
      minSize: 250,
      dataType: ColumnDataType.select,
      selectProps: preferenceColumn({
        subType: PreferenceGroupSubTypeEnum.FlightClass,
      }),
    },
    {
      header: t('common:route'),
      accessorKey: 'route',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:time'),
      accessorKey: 'time',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:price'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    ...deleteColumn(editable),
  ];
}

export default function ScheduleAirlineTable(props: Props) {
  const { editable } = useFormState();
  const name =
    `flightPurchases.${props.parentIndex}.flightPurchaseAirlines` as const;
  const { getValues, trigger, control, clearErrors } =
    useFormContext<TicketOrderFormType>();

  const columns = useGetScheduleAirlineTableList();

  const { update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const airlines = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const airline = airlines[currentRowIndex];
      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof TicketOrderFlightPurchaseAirlineFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<TicketOrderFlightPurchaseAirlineFormType> =
        () => {
          if (columnId === 'preferenceId') {
            return {
              preferenceName: extra?.[0]?.label as string,
            };
          }
          if (columnId === 'airlineId') {
            return {
              airlineName: extra?.[0]?.id as string,
            };
          }
          return {};
        };

      if (type === 'addRow') {
        append(addNewAirline());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...airline,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={getValues(name)}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
